import React, { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import {
  Box, Button, Paper, Typography, Tabs, Tab, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import AppShell from '../../../../components/layout/AppShell'
import PageHeader from '../../../../components/ui/PageHeader'
import FormattedCurrency from '../../../../components/ui/FormattedCurrency'

Show.layout = (page) => <AppShell>{page}</AppShell>

const CATEGORY_LABELS = { residential: 'Wohnung', commercial: 'Gewerbe', parking: 'Stellplatz', other: 'Sonstiges' }
const CONTRACT_LABELS = { OwnershipContract: 'Eigentum', LeaseContract: 'Miete', Vacancy: 'Leerstand' }

function TabPanel({ value, index, children }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null
}

export default function Show({ property, unit, attribute_values, contracts, generators }) {
  const { props } = usePage()
  const tenant = props.tenant || {}
  const [tab, setTab] = useState(0)

  return (
    <Box>
      <PageHeader
        title={`Einheit ${unit.number}`}
        breadcrumbs={[
          { label: 'Einheiten', href: `/m/${tenant.slug}/properties/${property.id}/units` },
          { label: unit.number },
        ]}
        actions={
          <Button variant="outlined" startIcon={<EditIcon />}
            onClick={() => router.visit(`/m/${tenant.slug}/properties/${property.id}/units/${unit.id}/edit`)}
            data-testid="edit-unit-btn"
          >
            Bearbeiten
          </Button>
        }
      />

      {/* Header info */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Chip size="small" label={CATEGORY_LABELS[unit.category] || unit.category} variant="outlined" />
          {unit.location && <Typography variant="body2" color="text.secondary">{unit.location}</Typography>}
          {unit.building_name && <Typography variant="body2" color="text.secondary">Gebäude: {unit.building_name}</Typography>}
          {unit.floor && <Typography variant="body2" color="text.secondary">Etage: {unit.floor}</Typography>}
          <Chip size="small" label={unit.active ? 'Aktiv' : 'Inaktiv'}
            color={unit.active ? 'success' : 'default'} variant="outlined" />
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
          <Tab label="Stammdaten" />
          <Tab label="Attribute" />
          <Tab label="Verträge" />
          <Tab label="Sollstellungen" />
        </Tabs>

        <Box sx={{ p: 2 }}>
          {/* Stammdaten */}
          <TabPanel value={tab} index={0}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, maxWidth: 480 }}>
              {[
                ['Nummer', unit.number],
                ['Kategorie', CATEGORY_LABELS[unit.category] || unit.category],
                ['Lage', unit.location || '–'],
                ['Etage', unit.floor || '–'],
                ['Gebäude', unit.building_name || '–'],
                ['Status', unit.active ? 'Aktiv' : 'Inaktiv'],
              ].map(([label, value]) => (
                <React.Fragment key={label}>
                  <Typography variant="body2" color="text.secondary">{label}</Typography>
                  <Typography variant="body2">{value}</Typography>
                </React.Fragment>
              ))}
            </Box>
          </TabPanel>

          {/* Attribute */}
          <TabPanel value={tab} index={1}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'background.subtle' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Attribut</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Wert</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Einheit</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Geschäftsjahr</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attribute_values.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">Keine Attributwerte.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {attribute_values.map((av) => (
                    <TableRow key={av.id}>
                      <TableCell>{av.attribute_type_name}</TableCell>
                      <TableCell>{av.numeric_value}</TableCell>
                      <TableCell>{av.unit_of_measure}</TableCell>
                      <TableCell>{av.fiscal_year_label}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Button
              size="small"
              sx={{ mt: 2 }}
              onClick={() => router.visit(`/m/${tenant.slug}/properties/${property.id}/units/attributes`)}
            >
              Attribute bulk bearbeiten
            </Button>
          </TabPanel>

          {/* Verträge */}
          <TabPanel value={tab} index={2}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'background.subtle' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Typ</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Kontakt</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Von</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Bis</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contracts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">Keine Verträge.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {contracts.map((c) => (
                    <TableRow key={c.id} hover sx={{ cursor: 'pointer' }}
                      onClick={() => router.visit(`/m/${tenant.slug}/properties/${property.id}/contracts/${c.id}`)}>
                      <TableCell>
                        <Chip size="small" label={CONTRACT_LABELS[c.type] || c.type} variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                      </TableCell>
                      <TableCell>{c.contact_name}</TableCell>
                      <TableCell>{c.start_date}</TableCell>
                      <TableCell>{c.end_date || '–'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Sollstellungen */}
          <TabPanel value={tab} index={3}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'background.subtle' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Typ</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Betrag</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Gültig ab</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Gültig bis</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Wiederkehrend</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {generators.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">Keine Sollstellungen.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {generators.map((g) => (
                    <TableRow key={g.id}>
                      <TableCell>{g.receivable_type}</TableCell>
                      <TableCell align="right">{g.amount ? <FormattedCurrency value={g.amount} /> : '–'}</TableCell>
                      <TableCell>{g.valid_from}</TableCell>
                      <TableCell>{g.valid_until || '–'}</TableCell>
                      <TableCell>{g.recurring ? 'Ja' : 'Nein'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        </Box>
      </Paper>
    </Box>
  )
}
